import { FormattedMessage, useIntl } from "react-intl";
import React, { useRef, useState } from "react";
import { Button, Card, Grid } from "@mui/material";
import { useToken } from "@packsize/authentication";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";

import { useCopyToClipboard } from "@/hooks";
import { withErrorBoundary } from "@/components/error-boundary";

import { ConfigurationPageLayout } from "../components/configuration-page-layout";

import { AddApiConnection } from "./modals/add-api-connection";
import { ServerSettingsTab, type ServerSettingsTabRef } from "./server-settings-tab";

interface ServerSettingsOutletContext {
	serverSettingsRef: React.RefObject<ServerSettingsTabRef>;
	setIsDirty: (dirty: boolean) => void;
}

export const ServerSettings = withErrorBoundary(() => {
	const intl = useIntl();
	const { decodedToken } = useToken();
	const location = useLocation();

	const [isDirty, setIsDirty] = useState(false);
	const serverSettingsRef = useRef<ServerSettingsTabRef>(null);

	const copyToClipboard = useCopyToClipboard();

	const isApiTab = location.pathname.endsWith("/api");

	const actions = (
		<>
			{isApiTab === false && (
				<Button
					variant="contained"
					disabled={!isDirty}
					onClick={async () => await serverSettingsRef.current?.save()}
					data-pendoid="server-settings__button__save"
					data-cy="save-settings"
				>
					<FormattedMessage id="Save" defaultMessage="Save" />
				</Button>
			)}
			{isApiTab === true && (
				<>
					<Button
						variant="outlined"
						onClick={() => copyToClipboard(decodedToken?.tid ?? "")}
						data-pendoid="api-settings__button__copy-tenant-id"
					>
						<FormattedMessage id="Copy Tenant ID" defaultMessage="Copy Tenant ID" />
					</Button>
					<AddApiConnection />
				</>
			)}
		</>
	);

	const tabs = [
		{ label: "General", link: "settings" },
		{ label: "API", link: "api" },
	];

	return (
		<ConfigurationPageLayout
			title={intl.formatMessage({ id: "Settings", defaultMessage: "Settings" })}
			actions={actions}
			tabs={tabs}
		>
			<Card component={Grid} container direction="column" sx={{ overflowY: "auto" }}>
				<Outlet context={{ serverSettingsRef, setIsDirty } satisfies ServerSettingsOutletContext} />
			</Card>
		</ConfigurationPageLayout>
	);
});

export const ServerSettingsTabRoute = () => {
	const { serverSettingsRef, setIsDirty } = useOutletContext<ServerSettingsOutletContext>();
	return <ServerSettingsTab ref={serverSettingsRef} setIsDirty={setIsDirty} />;
};
